import { type ReactNode, type CSSProperties } from 'react'

// ── Design tokens ──────────────────────────────────────────────────────────────
const c = {
  surface:         '#f7f9fb',
  surfaceLowest:   '#ffffff',
  surfaceLow:      '#f2f4f6',
  surfaceContainer:'#eceef0',
  surfaceHigh:     '#e6e8ea',
  onSurface:       '#191c1e',
  onSurfaceVar:    '#464555',
  outlineVar:      '#c7c4d8',
  primary:         '#3525cd',
  primaryContainer:'#4f46e5',
  onPrimary:       '#ffffff',
  secondary:       '#58579b',
  tertiary:        '#7e3000',
  error:           '#ba1a1a',
  shadow:          '0 2px 8px rgba(25,28,30,0.06)',
  shadowMd:        '0 12px 40px rgba(25,28,30,0.07)',
} as const

// ── Page shell ────────────────────────────────────────────────────────────────
export function PageHeader({
  title, subtitle, actions
}: { title: string; subtitle?: string; actions?: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 32 }}>
      <div>
        <h1 style={{
          fontSize: 24, fontWeight: 700, margin: 0, letterSpacing: '-0.5px',
          color: c.onSurface, fontFamily: 'Manrope, sans-serif',
        }}>{title}</h1>
        {subtitle && <p style={{ color: c.onSurfaceVar, fontSize: 14, marginTop: 4, margin: '4px 0 0' }}>{subtitle}</p>}
      </div>
      {actions && <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>{actions}</div>}
    </div>
  )
}

// ── Card ──────────────────────────────────────────────────────────────────────
export function Card({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div style={{
      background: c.surfaceLowest,
      borderRadius: 12,
      padding: 20,
      boxShadow: c.shadow,
      ...style,
    }}>
      {children}
    </div>
  )
}

// ── Stat card ─────────────────────────────────────────────────────────────────
export function StatCard({
  label, value, sub, subColor
}: { label: string; value: string | number; sub?: string; subColor?: string }) {
  return (
    <Card>
      <div style={{ fontSize: 12, color: c.onSurfaceVar, marginBottom: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ fontSize: 30, fontWeight: 700, letterSpacing: '-0.5px', color: c.onSurface, fontFamily: 'Manrope, sans-serif' }}>{value}</div>
      {sub && <div style={{ fontSize: 13, color: subColor ?? c.onSurfaceVar, marginTop: 6 }}>{sub}</div>}
    </Card>
  )
}

// ── Button ────────────────────────────────────────────────────────────────────
interface BtnProps {
  children: ReactNode; onClick?: () => void; variant?: 'primary' | 'outline' | 'ghost' | 'danger'
  size?: 'sm' | 'md'; disabled?: boolean; type?: 'button' | 'submit'; style?: CSSProperties
}
export function Button({ children, onClick, variant = 'primary', size = 'md', disabled, type = 'button', style }: BtnProps) {
  const base: CSSProperties = {
    border: 'none', borderRadius: 8, cursor: disabled ? 'not-allowed' : 'pointer',
    fontWeight: 500, fontFamily: 'Inter, sans-serif', display: 'inline-flex', alignItems: 'center',
    gap: 6, opacity: disabled ? 0.5 : 1, transition: 'all 0.15s',
    padding: size === 'sm' ? '6px 12px' : '9px 16px',
    fontSize: size === 'sm' ? 13 : 14,
  }
  const variants: Record<string, CSSProperties> = {
    primary: { background: c.primary, color: '#fff' },
    outline: { background: 'transparent', color: c.onSurface, border: `1px solid ${c.outlineVar}` },
    ghost:   { background: 'transparent', color: c.onSurfaceVar },
    danger:  { background: 'transparent', color: c.error, border: `1px solid ${c.error}` },
  }
  return (
    <button type={type} onClick={onClick} disabled={disabled}
      style={{ ...base, ...variants[variant], ...style }}>
      {children}
    </button>
  )
}

// ── Badge ─────────────────────────────────────────────────────────────────────
const TAG_COLORS: Record<string, { bg: string; text: string }> = {
  bug:         { bg: '#ffdad6', text: '#93000a' },
  feature:     { bg: '#dde3ff', text: '#001a72' },
  praise:      { bg: '#c8f0d8', text: '#003920' },
  performance: { bg: '#ffdcc2', text: '#4e1700' },
  other:       { bg: '#eceef0', text: '#464555' },
  open:        { bg: '#dde3ff', text: '#001a72' },
  resolved:    { bg: '#c8f0d8', text: '#003920' },
}
export function Badge({ label }: { label: string }) {
  const col = TAG_COLORS[label.toLowerCase()] ?? TAG_COLORS['other']!
  return (
    <span style={{
      background: col.bg, color: col.text,
      padding: '3px 8px', borderRadius: 6, fontSize: 12, fontWeight: 500,
      textTransform: 'capitalize', display: 'inline-block',
    }}>{label}</span>
  )
}

// ── Stars ─────────────────────────────────────────────────────────────────────
export function Stars({ rating, size = 14 }: { rating: number | null; size?: number }) {
  if (!rating) return <span style={{ color: c.outlineVar, fontSize: size }}>—</span>
  return (
    <span style={{ color: '#F59E0B', fontSize: size, letterSpacing: 1 }}>
      {'★'.repeat(rating)}{'☆'.repeat(5 - rating)}
    </span>
  )
}

// ── Input ─────────────────────────────────────────────────────────────────────
interface InputProps {
  label?: string; value: string; onChange: (v: string) => void
  placeholder?: string; type?: string; style?: CSSProperties
}
export function Input({ label, value, onChange, placeholder, type = 'text', style }: InputProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {label && <label style={{ fontSize: 13, color: c.onSurfaceVar, fontWeight: 500 }}>{label}</label>}
      <input
        type={type} value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          background: c.surfaceContainer, border: 'none', borderRadius: 8,
          padding: '9px 12px', color: c.onSurface, fontSize: 14, fontFamily: 'Inter, sans-serif',
          outline: 'none', width: '100%', ...style,
        }}
      />
    </div>
  )
}

// ── Spinner ───────────────────────────────────────────────────────────────────
export function Spinner() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
      <div style={{
        width: 24, height: 24, border: `2px solid ${c.surfaceHigh}`,
        borderTopColor: c.primary, borderRadius: '50%',
        animation: 'spin 0.7s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}

// ── Empty state ───────────────────────────────────────────────────────────────
export function Empty({ icon, title, sub, action }: {
  icon: string; title: string; sub?: string; action?: ReactNode
}) {
  return (
    <div style={{ textAlign: 'center', padding: '60px 20px' }}>
      <div style={{ fontSize: 40, marginBottom: 16 }}>{icon}</div>
      <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 8, color: c.onSurface, fontFamily: 'Manrope, sans-serif' }}>{title}</div>
      {sub && <div style={{ color: c.onSurfaceVar, fontSize: 14, marginBottom: 20 }}>{sub}</div>}
      {action}
    </div>
  )
}

// ── Confirm Modal ─────────────────────────────────────────────────────────────
export function ConfirmModal({ title, message, confirmLabel = 'Confirm', danger = false, onConfirm, onCancel }: {
  title: string; message: string; confirmLabel?: string; danger?: boolean
  onConfirm: () => void; onCancel: () => void
}) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(25,28,30,0.4)',
      backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
    }}>
      <div style={{
        background: '#ffffff', borderRadius: 16,
        boxShadow: '0 24px 60px rgba(25,28,30,0.15)',
        padding: 32, width: 400,
      }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#191c1e', fontFamily: 'Manrope, sans-serif', margin: '0 0 8px' }}>{title}</h2>
        <p style={{ fontSize: 14, color: '#777587', margin: '0 0 24px', lineHeight: 1.6 }}>{message}</p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm}>{confirmLabel}</Button>
        </div>
      </div>
    </div>
  )
}

// ── Time ──────────────────────────────────────────────────────────────────────
export function timeAgo(ts: number): string {
  const diff = Date.now() / 1000 - ts
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`
  return new Date(ts * 1000).toLocaleDateString()
}
